import React from 'react'
import config from '../../config'

declare global {
  interface Window {
    chatwootSettings: {
      hideMessageBubble: boolean
      position: string
      type: string
    }
    chatwootSDK: {
      run: (config: { websiteToken: string; baseUrl: string }) => void
    }
    $chatwoot?: {
      reset: () => void
      toggle?: (state?: 'open' | 'close') => void
      toggleBubbleVisibility?: (visibility: 'show' | 'hide') => void
    }
  }
}

const BASE_URL = 'https://app.chatwoot.com'
const SCRIPT_ID = 'chatwoot-sdk'

const BUBBLE_SELECTORS = ['#cw-bubble-holder', '.woot--bubble-holder', '.woot-widget-bubble']
const PANEL_SELECTORS = ['#cw-widget-holder', '#cw-widget-translator', '#chatwoot_live_chat_widget', '.woot-widget-holder']

let mountedChatwootHosts = 0

function setDisplay(selectors: string[], visible: boolean) {
  selectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((node) => {
      (node as HTMLElement).style.display = visible ? '' : 'none'
    })
  })
}

function setChatwootRouteVisibility(visible: boolean) {
  const chatwoot = window.$chatwoot
  const bubbleHolder = document.querySelector('.woot--bubble-holder, #cw-bubble-holder')

  if (chatwoot) {
    try {
      if (!visible) chatwoot.toggle?.('close')
      if (bubbleHolder) {
        chatwoot.toggleBubbleVisibility?.(visible ? 'show' : 'hide')
      }
    } catch {
      // Chatwoot throws if the bubble holder is not in the DOM yet.
    }
  }

  setDisplay(BUBBLE_SELECTORS, visible)
  setDisplay(PANEL_SELECTORS, visible)
}

function applyChatwootVisibilityForCurrentRoute() {
  setChatwootRouteVisibility(mountedChatwootHosts > 0)
}

function startChatwootSdk() {
  if (!window.chatwootSDK || window.$chatwoot) return
  window.chatwootSDK.run({
    websiteToken: config.REACT_APP_CHATWOOT_TOKEN,
    baseUrl: BASE_URL,
  })
}

function ensureChatwootScript() {
  const existingScript = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
  if (existingScript) {
    startChatwootSdk()
    applyChatwootVisibilityForCurrentRoute()
    return
  }

  const script = document.createElement('script')
  script.id = SCRIPT_ID
  script.src = `${BASE_URL}/packs/js/sdk.js`
  script.async = true
  script.defer = true
  script.onload = () => {
    startChatwootSdk()
    applyChatwootVisibilityForCurrentRoute()
  }

  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.body.appendChild(script)
  }
}

const Chatwoot = () => {
  React.useEffect(() => {
    mountedChatwootHosts += 1

    window.chatwootSettings = {
      hideMessageBubble: false,
      position: 'right',
      type: 'standard',
    }

    window.addEventListener('chatwoot:ready', applyChatwootVisibilityForCurrentRoute)
    ensureChatwootScript()
    applyChatwootVisibilityForCurrentRoute()

    return () => {
      mountedChatwootHosts -= 1
      window.removeEventListener('chatwoot:ready', applyChatwootVisibilityForCurrentRoute)
      applyChatwootVisibilityForCurrentRoute()
    }
  }, [])

  return null
}

export default Chatwoot
